import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, FileText, CheckCircle2, XCircle, Search, Download, Paperclip, Maximize, X, Clock, RotateCcw } from 'lucide-react';
import {
  getAssignmentById,
  getAllSubmissions,
  gradeSubmission,
  revertSubmission
} from '../../../services/assignment.service';
import { getCoursesById } from '../../../services/course.service';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCourse } from '../../../context/CourseContext';
import { motion } from 'framer-motion';
import { cn } from '../../../lib/utils';
import {
  card,
  cardInteractive,
  surfaceMuted,
  pageBg,
  emptyState,
  pageTitle,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  badgeBase,
  statusStyles,
  dividerInner,
  iconSize,
  motionFadeUp,
  motionStaggerChild,
  motionHoverLift,
} from '../../../lib/design-system';

// ---------------------------------------------------------------------------
// Shared style constants
// ---------------------------------------------------------------------------
const inputClasses =
  "w-full rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1/50 focus:border-transparent p-3 text-sm";
const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecondary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const TeacherAssignmentGrading = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { courseData, setCourseData } = useCourse();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [grades, setGrades] = useState({});
  const [feedback, setFeedback] = useState({});
  const [questionGrades, setQuestionGrades] = useState({}); // { [studentKey]: { [questionId]: score } }
  const [sortBy, setSortBy] = useState('all'); // all, submitted, unsubmitted
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('answers'); // 'answers' or 'file'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [useAutoCalculate, setUseAutoCalculate] = useState(true);
  const [manualSubjectiveGrades, setManualSubjectiveGrades] = useState({}); // { [studentKey]: number }
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertReason, setRevertReason] = useState('');

  // Fetch assignment and submissions
  const fetchData = useCallback(async () => {
    if (!assignmentId) {
      console.log('[TeacherAssignmentGrading] No assignmentId provided, skipping fetch');
      return;
    }

    try {
      setIsLoading(true);
      console.log('[TeacherAssignmentGrading] Starting to fetch assignment data for ID:', assignmentId);

      // Fetch assignment
      console.log('[TeacherAssignmentGrading] Fetching assignment by ID...');
      const assignmentResponse = await getAssignmentById({ assignmentID: assignmentId });
      const assign = assignmentResponse.assignment;
      console.log('[TeacherAssignmentGrading] Assignment fetched:', {
        id: assign._id,
        title: assign.title,
        questionsCount: assign.questions?.length || 0
      });
      setAssignment(assign);

      // If courseData.students is empty (e.g., on page refresh), fetch course data
      if (!courseData?.students || courseData.students.length === 0) {
        const courseId = typeof assign.course === 'object' ? assign.course?._id : assign.course;
        if (courseId) {
          console.log('[TeacherAssignmentGrading] CourseData.students is empty, fetching course data for ID:', courseId);
          try {
            const courseResponse = await getCoursesById(courseId);
            console.log('[TeacherAssignmentGrading] Course data fetched:', {
              id: courseResponse._id,
              studentsCount: courseResponse.students?.length || 0
            });
            // Update the course context with fetched data
            setCourseData(prev => ({
              ...prev,
              ...courseResponse,
              _id: courseResponse._id,
              id: courseResponse._id,
              students: courseResponse.students || []
            }));
          } catch (courseError) {
            console.error('[TeacherAssignmentGrading] Error fetching course data:', courseError);
          }
        }
      }

      // Fetch all submissions (without filters - filtering will be done on frontend)
      console.log('[TeacherAssignmentGrading] Fetching all submissions...');
      const submissionsResponse = await getAllSubmissions(assignmentId, {});
      const subs = submissionsResponse.submissions || [];
      console.log('[TeacherAssignmentGrading] Submissions fetched:', {
        count: subs.length,
        submissions: subs.map(sub => ({
          id: sub._id,
          userId: sub.userId,
          studentId: sub.studentId,
          userName: sub.userName,
          userEmail: sub.userEmail
        }))
      });
      setSubmissions(subs);

      // Initialize grades and feedback from existing submissions
      const objectiveQuestions = assign.questions?.filter(q => q.type === 'objective') || [];
      const subjectiveQuestions = assign.questions?.filter(q => q.type === 'subjective') || [];
      const initialGrades = {};
      const initialFeedback = {};
      const initialQuestionGrades = {};

      console.log('[TeacherAssignmentGrading] Processing submissions for grades and feedback...');
      subs.forEach(sub => {
        // Use userId or studentId from submission
        const studentId = sub.userId || sub.studentId;
        console.log('[TeacherAssignmentGrading] Processing submission:', {
          submissionId: sub._id,
          userId: sub.userId,
          studentId: sub.studentId,
          userName: sub.userName,
          grade: sub.grade,
          feedback: sub.feedback
        });

        // Use grade field as total grade
        if (sub.grade !== null && sub.grade !== undefined) {
          initialGrades[studentId] = sub.grade;
          console.log('[TeacherAssignmentGrading] Found grade:', { studentId, grade: sub.grade });
        }

        // Use feedback field
        if (sub.feedback) {
          initialFeedback[studentId] = sub.feedback;
          console.log('[TeacherAssignmentGrading] Found feedback for student:', studentId);
        }

        // Initialize per-question grades
        if (sub.questionGrades && Object.keys(sub.questionGrades).length > 0) {
          initialQuestionGrades[studentId] = { ...sub.questionGrades };
        }
      });

      console.log('[TeacherAssignmentGrading] Initial grades:', initialGrades);
      console.log('[TeacherAssignmentGrading] Initial feedback:', Object.keys(initialFeedback).length, 'students');
      setGrades(initialGrades);
      setFeedback(initialFeedback);
      setQuestionGrades(initialQuestionGrades);
    } catch (error) {
      console.error('[TeacherAssignmentGrading] Error fetching assignment data:', error);
      toast.error('Failed to load assignment data');
    } finally {
      setIsLoading(false);
      console.log('[TeacherAssignmentGrading] Fetch completed');
    }
  }, [assignmentId]); // Only depends on assignmentId, not filters

  useEffect(() => {
    fetchData();
  }, [fetchData]); // fetchData is memoized and only changes when assignmentId changes

  // Helper function to get the grade key for a student (from submission userId/studentId)
  const getGradeKey = (student) => {
    if (!student?.submission) return student?.id;
    return student.submission.userId || student.submission.studentId || student.id;
  };

  // Sync grades and feedback from submission data when selectedStudent changes
  useEffect(() => {
    if (selectedStudent?.submission) {
      const gradeKey = getGradeKey(selectedStudent);
      const submissionGrade = selectedStudent.submission.grade;
      const submissionFeedback = selectedStudent.submission.feedback;

      console.log('[TeacherAssignmentGrading] Syncing grades/feedback from submission:', {
        gradeKey,
        submissionGrade,
        submissionFeedback
      });

      // Update grades if not already set or if submission has a grade
      if (submissionGrade !== null && submissionGrade !== undefined) {
        setGrades(prev => {
          if (prev[gradeKey] === undefined || prev[gradeKey] === null) {
            return { ...prev, [gradeKey]: submissionGrade };
          }
          return prev;
        });
      }

      // Update feedback if not already set or if submission has feedback
      if (submissionFeedback) {
        setFeedback(prev => {
          if (!prev[gradeKey]) {
            return { ...prev, [gradeKey]: submissionFeedback };
          }
          return prev;
        });
      }

      // Sync per-question grades from submission
      const submissionQuestionGrades = selectedStudent.submission.questionGrades;
      if (submissionQuestionGrades && Object.keys(submissionQuestionGrades).length > 0) {
        setQuestionGrades(prev => {
          if (!prev[gradeKey]) {
            return { ...prev, [gradeKey]: { ...submissionQuestionGrades } };
          }
          return prev;
        });
      }
    }
  }, [selectedStudent]);

  // Determine assignment type from first question
  const assignmentType = assignment?.questions?.[0]?.type || null;
  const subjectiveQuestions = assignment?.questions?.filter(q => q.type === 'subjective') || [];
  const objectiveQuestions = assignment?.questions?.filter(q => q.type === 'objective') || [];

  console.log('[TeacherAssignmentGrading] Assignment type determined from first question:', assignmentType);
  console.log('[TeacherAssignmentGrading] Subjective questions:', subjectiveQuestions.length);
  console.log('[TeacherAssignmentGrading] Objective questions:', objectiveQuestions.length);

  // Helper function to get file type from submission file URL
  const getFileType = (fileUrl) => {
    if (!fileUrl) return 'unknown';
    const url = fileUrl.toLowerCase();
    if (url.endsWith('.pdf')) return 'pdf';
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(url)) return 'image';
    if (url.endsWith('.ppt') || url.endsWith('.pptx')) return 'ppt';
    if (url.endsWith('.doc') || url.endsWith('.docx')) return 'doc';
    return 'other';
  };

  // Render file viewer content
  const renderFileViewer = (fileUrl, fileName, showFullscreenButton = false) => {
    if (!fileUrl) return null;

    const fileType = getFileType(fileUrl);

    if (fileType === 'pdf') {
      return (
        <div className="relative w-full h-full">
          {showFullscreenButton && (
            <button
              onClick={() => setIsFullscreen(true)}
              className={cn("absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 p-2 rounded-sm shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors")}
              title="Fullscreen"
            >
              <Maximize className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=1`}
            className="w-full h-full border-0"
            title={fileName}
          />
        </div>
      );
    } else if (fileType === 'image') {
      return (
        <div className={cn(surfaceMuted, "flex items-center justify-center h-full p-4 relative")}>
          {showFullscreenButton && (
            <button
              onClick={() => setIsFullscreen(true)}
              className={cn("absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 p-2 rounded-sm shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors")}
              title="Fullscreen"
            >
              <Maximize className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
          )}
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain rounded-sm shadow-lg"
          />
        </div>
      );
    } else if (fileType === 'ppt' || fileType === 'pptx') {
      return (
        <div className={cn(surfaceMuted, "flex flex-col items-center justify-center h-full p-6")}>
          <div className={cn(card, "p-6 max-w-md w-full")}>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-orange-500 text-white text-sm flex items-center justify-center w-10 h-10 rounded-sm">
                PPT
              </div>
              <h3 className="ml-3 font-bold text-lg dark:text-white">{fileName}</h3>
            </div>
            <p className={cn(bodySecondary, "mb-6")}>
              PowerPoint presentations cannot be previewed directly. You can download the file to view it.
            </p>
            <div className="flex justify-center">
              <a
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className={btnPrimary}
              >
                <FileText className="h-5 w-5" />
                Download Presentation
              </a>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={cn(surfaceMuted, "flex flex-col items-center justify-center h-full p-6")}>
          <div className={cn(card, "p-6 max-w-md w-full")}>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gray-500 text-white text-sm flex items-center justify-center w-10 h-10 rounded-sm">
                FILE
              </div>
              <h3 className="ml-3 font-bold text-lg dark:text-white">{fileName}</h3>
            </div>
            <p className={cn(bodySecondary, "mb-6")}>
              This file type cannot be previewed. You can download it to view.
            </p>
            <div className="flex justify-center">
              <a
                href={fileUrl}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className={btnPrimary}
              >
                <FileText className="h-5 w-5" />
                Download File
              </a>
            </div>
          </div>
        </div>
      );
    }
  };

  // Reset tab when student changes
  useEffect(() => {
    if (selectedStudent?.submission?.submissionFile) {
      setActiveTab('file'); // Default to file tab if file exists
    } else {
      setActiveTab('answers');
    }
  }, [selectedStudent?.id]);

  // Get students from courseData and map with their submissions
  // Match student.id from courseData.students with submission.userId or submission.studentId
  console.log('[TeacherAssignmentGrading] Mapping students with submissions...');
  console.log('[TeacherAssignmentGrading] courseData.students:', courseData?.students);
  console.log('[TeacherAssignmentGrading] submissions:', submissions.map(sub => ({
    id: sub._id,
    userId: sub.userId,
    studentId: sub.studentId,
    userName: sub.userName
  })));

  // Filter students by assignment branches (if specific branches are set)
  const assignmentBranches = assignment?.branches || [];
  const hasBranchFilter = assignmentBranches.length > 0 && !assignmentBranches.includes('ALL');
  const branchFilteredStudents = hasBranchFilter
    ? (courseData?.students || []).filter(student => student.branch && assignmentBranches.includes(student.branch))
    : (courseData?.students || []);

  const studentSubmissions = branchFilteredStudents.map(student => {
    console.log('[TeacherAssignmentGrading] Processing student:', {
      studentId: student.id,
      studentName: student.name,
      studentRollNo: student.rollNo
    });

    // Find submission by matching student.id with submission.userId or submission.studentId
    const submission = submissions.find(sub => {
      const matchesUserId = sub.userId === student.id;
      const matchesStudentId = sub.studentId === student.id;
      const match = matchesUserId || matchesStudentId;

      if (match) {
        console.log('[TeacherAssignmentGrading] Found matching submission for student:', {
          studentId: student.id,
          studentName: student.name,
          submissionId: sub._id,
          submissionUserId: sub.userId,
          submissionStudentId: sub.studentId,
          matchedBy: matchesUserId ? 'userId' : 'studentId'
        });
      }

      return match;
    });

    const hasGrade = submission && (submission.status === 'graded' || (submission.grade !== null && submission.grade !== undefined));

    console.log('[TeacherAssignmentGrading] Student submission mapping result:', {
      studentId: student.id,
      studentName: student.name,
      hasSubmission: !!submission,
      submissionId: submission?._id,
      hasGrade: hasGrade
    });

    return {
      id: student.id,
      name: student.name || 'Unknown Student',
      rollNo: student.rollNo || student.email || '',
      email: student.email || '',
      submission: submission || null,
      status: submission ? 'submitted' : 'not_submitted',
      hasGrade: hasGrade
    };
  });

  console.log('[TeacherAssignmentGrading] Final studentSubmissions:', studentSubmissions.map(s => ({
    id: s.id,
    name: s.name,
    hasSubmission: !!s.submission,
    status: s.status
  })));

  // Extract available branches from the filtered students (respects assignment branches)
  const availableBranches = [...new Set(
    branchFilteredStudents.map(s => s.branch).filter(Boolean)
  )].sort();

  // Filter students
  const filteredStudents = studentSubmissions.filter(student => {
    // Search filter - search by name, email, or rollNo
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesName = student.name.toLowerCase().includes(searchLower);
      const matchesEmail = student.email.toLowerCase().includes(searchLower);
      const matchesRollNo = student.rollNo && student.rollNo.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesEmail && !matchesRollNo) {
        return false;
      }
    }

    // Branch filter
    if (branchFilter !== 'all') {
      const studentData = (courseData?.students || []).find(s => s.id === student.id);
      if (studentData?.branch !== branchFilter) return false;
    }

    // Sort filter
    if (sortBy === 'submitted' && !student.submission) return false;
    if (sortBy === 'unsubmitted' && student.submission) return false;

    return true;
  });

  console.log('[TeacherAssignmentGrading] Filtered students:', {
    total: studentSubmissions.length,
    filtered: filteredStudents.length,
    searchTerm,
    sortBy
  });
  console.log(courseData, assignment?.submissions);

  const handleGradeChange = (studentId, value) => {
    console.log('[TeacherAssignmentGrading] Grade changed:', { studentId, value });
    setGrades(prev => {
      const newGrades = {
        ...prev,
        [studentId]: value ? parseFloat(value) : null
      };
      console.log('[TeacherAssignmentGrading] Updated grades:', newGrades);
      return newGrades;
    });
  };

  const handleFeedbackChange = (studentId, value) => {
    console.log('[TeacherAssignmentGrading] Feedback changed:', { studentId, valueLength: value?.length });
    setFeedback(prev => {
      const newFeedback = {
        ...prev,
        [studentId]: value
      };
      console.log('[TeacherAssignmentGrading] Updated feedback:', Object.keys(newFeedback).length, 'students');
      return newFeedback;
    });
  };

  const handleSaveGrade = async (student) => {
    console.log('[TeacherAssignmentGrading] Saving grade for student:', {
      studentId: student.id,
      studentName: student.name,
      hasSubmission: !!student.submission
    });

    // Check if assignment is ungraded
    if (assignment.isUngraded) {
      console.log('[TeacherAssignmentGrading] Cannot save grade - assignment is ungraded');
      toast.error('This assignment is ungraded and cannot be scored');
      return;
    }

    if (!student.submission) {
      console.log('[TeacherAssignmentGrading] Cannot save grade - student has not submitted');
      toast.error('Student has not submitted the assignment');
      return;
    }

    const submissionId = student.submission._id;
    const gradeKey = getGradeKey(student);
    const totalGrade = grades[gradeKey] ?? student.submission?.grade;
    const studentFeedback = feedback[gradeKey] ?? student.submission?.feedback ?? '';

    console.log('[TeacherAssignmentGrading] Grade data to save:', {
      submissionId,
      studentId: student.id,
      gradeKey,
      grade: totalGrade,
      feedback: studentFeedback,
      submissionGrade: student.submission?.grade,
      submissionFeedback: student.submission?.feedback
    });

    // Validate grades for subjective assignments
    if (subjectiveQuestions.length > 0) {
      if (useAutoCalculate) {
        // Auto-calculate mode — validate per-question grades
        const qg = questionGrades[gradeKey];
        const hasQuestionGrades = qg && subjectiveQuestions.some(q => qg[q._id] !== null && qg[q._id] !== undefined);

        if (!hasQuestionGrades) {
          console.log('[TeacherAssignmentGrading] Validation failed - no per-question grades entered');
          toast.error('Please enter grades for the subjective questions');
          return;
        }

        // Validate each score is within range
        for (const q of subjectiveQuestions) {
          const score = qg?.[q._id];
          if (score !== null && score !== undefined) {
            if (score < 0 || score > (q.points || 0)) {
              toast.error(`Score for Q${subjectiveQuestions.indexOf(q) + 1} must be between 0 and ${q.points}`);
              return;
            }
          }
        }
      } else {
        // Manual mode — validate the manual subjective grade
        const manualValue = manualSubjectiveGrades[gradeKey];
        if (manualValue === null || manualValue === undefined) {
          toast.error('Please enter a subjective grade');
          return;
        }
        const maxSubjective = getTotalSubjectivePoints();
        if (manualValue < 0 || manualValue > maxSubjective) {
          toast.error(`Subjective grade must be between 0 and ${maxSubjective}`);
          return;
        }
      }
    }

    // For objective assignments, grade is optional (can use auto-calculated score or override)

    try {
      setIsLoading(true);
      console.log('[TeacherAssignmentGrading] Calling gradeSubmission API...');

      // Prepare grade data
      const gradeData = {
        feedback: studentFeedback,
      };

      // Build grade payload based on grading mode
      if (subjectiveQuestions.length > 0 && !useAutoCalculate) {
        // Manual mode — send total grade directly
        const objectiveScore = calculateObjectiveScore(student.submission) || 0;
        const manualSubjective = manualSubjectiveGrades[gradeKey] || 0;
        gradeData.grade = objectiveScore + manualSubjective;
        gradeData.subjectiveGrade = manualSubjective;
      } else if (subjectiveQuestions.length > 0) {
        // Auto-calculate mode — send per-question grades
        const qg = questionGrades[gradeKey];
        if (qg && Object.keys(qg).length > 0) {
          const qgPayload = {};
          subjectiveQuestions.forEach(q => {
            if (qg[q._id] !== null && qg[q._id] !== undefined) {
              qgPayload[q._id] = Number(qg[q._id]);
            }
          });
          gradeData.questionGrades = qgPayload;
        }
      } else {
        // Fallback to total grade for objective-only assignments
        gradeData.grade = totalGrade || 0;
      }

      console.log('[TeacherAssignmentGrading] Grade data payload:', gradeData);

      // Optionally set total grade if needed (backend will calculate if not provided)
      // The backend calculates: grade = objectiveScore + subjectiveGrade (or uses provided grade)

      await gradeSubmission(assignmentId, submissionId, gradeData);
      console.log('[TeacherAssignmentGrading] Grade saved successfully');

      // Refresh data
      console.log('[TeacherAssignmentGrading] Refreshing data after save...');
      await fetchData();

      // Success message is handled in the service
    } catch (error) {
      console.error('[TeacherAssignmentGrading] Error saving grade:', error);
      // Error is already handled in the service with toast
    } finally {
      setIsLoading(false);
      console.log('[TeacherAssignmentGrading] Save grade operation completed');
    }
  };

  const calculateObjectiveScore = (submission) => {
    if (!submission) {
      console.log('[TeacherAssignmentGrading] calculateObjectiveScore: No submission provided');
      return 0;
    }
    // Use stored objectiveScore from API (auto-calculated on submission)
    const score = submission.objectiveScore || 0;
    console.log('[TeacherAssignmentGrading] calculateObjectiveScore:', { submissionId: submission._id, score });
    return score;
  };

  const getTotalObjectivePoints = () => {
    return objectiveQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
  };

  const getTotalSubjectivePoints = () => {
    return subjectiveQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
  };

  const getSubjectiveAutoTotal = (studentKey) => {
    const qg = questionGrades[studentKey];
    if (!qg) return 0;
    return subjectiveQuestions.reduce((sum, q) => sum + (Number(qg[q._id]) || 0), 0);
  };

  // ---------------------------------------------------------------------------
  // Loading skeleton
  // ---------------------------------------------------------------------------
  if (isLoading || !assignment) {
    return (
      <div className={cn(pageBg, "p-6")}>
        <div className={cn(card, "p-6 mb-6")}>
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-1/3 bg-gray-200 dark:bg-gray-700 rounded-sm" />
            <div className="h-4 w-1/4 bg-gray-200 dark:bg-gray-700 rounded-sm" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={cn(card, "p-4")}>
            <div className="animate-pulse space-y-3">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              ))}
            </div>
          </div>
          <div className={cn(card, "lg:col-span-2 p-6")}>
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-1/2 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(pageBg)}>
      {/* Header */}
      <div className={cn(card, "border-b px-6 py-4")}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={pageTitle}>{assignment.title}</h1>
            <div className={cn(accentBarSection, "mt-2")} />
            <p className={cn(bodySecondary, "mt-2")}>Grade student submissions</p>
          </div>
          <button
            onClick={() => {
              // Get courseID from assignment or courseData
              // Handle case where assignment.course might be a populated object or a string ID
              const assignmentCourse = assignment?.course;
              const courseId = (typeof assignmentCourse === 'object' ? assignmentCourse?._id : assignmentCourse)
                || courseData?._id
                || courseData?.id;

              if (courseId) {
                // Navigate back to the course page
                // The CourseManagement component will read from localStorage to restore the section
                navigate(`/teacher/course/${courseId}`);
              } else {
                // Fallback to browser back
                navigate(-1);
              }
            }}
            className={btnSecondary}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Student List */}
          <div className={cn(card, "lg:col-span-1 p-4")}>
            <div className="mb-4">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={cn(inputClasses, "pl-10")}
                />
              </div>

              {/* Filter tabs — editorial bottom-border style */}
              <div className={cn("flex gap-1 border-b mb-3", dividerInner)}>
                <button
                  onClick={() => setSortBy('all')}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium border-b-2 transition-colors",
                    sortBy === 'all'
                      ? "border-accent1 text-accent1"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
                  )}
                >
                  All
                </button>
                <button
                  onClick={() => setSortBy('submitted')}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium border-b-2 transition-colors",
                    sortBy === 'submitted'
                      ? "border-emerald-500 text-emerald-600"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
                  )}
                >
                  Submitted
                </button>
                <button
                  onClick={() => setSortBy('unsubmitted')}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium border-b-2 transition-colors",
                    sortBy === 'unsubmitted'
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700"
                  )}
                >
                  Unsubmitted
                </button>
              </div>

              {/* Branch filter */}
              {availableBranches.length > 0 && (
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className={cn(inputClasses, "mt-2 text-sm")}
                >
                  <option value="all">All Branches</option>
                  {availableBranches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Student cards */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredStudents.length === 0 ? (
                <div className={cn(emptyState)}>
                  <p className="text-gray-500 dark:text-gray-400">No students found</p>
                  {courseData?.students?.length === 0 && (
                    <p className={cn(bodySecondary, "mt-2")}>No students in course data</p>
                  )}
                </div>
              ) : (
                filteredStudents.map((student, index) => {
                  console.log('[TeacherAssignmentGrading] Rendering student in list:', {
                    id: student.id,
                    name: student.name,
                    status: student.status,
                    hasGrade: student.hasGrade
                  });

                  return (
                    <motion.div
                      key={student.id}
                      {...motionStaggerChild(index)}
                      whileHover={motionHoverLift}
                      onClick={() => {
                        console.log('[TeacherAssignmentGrading] Student selected:', {
                          id: student.id,
                          name: student.name,
                          hasSubmission: !!student.submission
                        });
                        setSelectedStudent(student);
                      }}
                      className={cn(
                        cardInteractive,
                        "p-3 cursor-pointer",
                        selectedStudent?.id === student.id
                          ? "bg-accent1/10 border-accent1"
                          : ""
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{student.name}</div>
                          <div className={cn(bodySecondary, "dark:text-gray-400")}>{student.rollNo}</div>
                          {student.submission?.submissionDate && (
                            <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                              <Clock size={12} />
                              <span>{new Date(student.submission.submissionDate).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-2">
                            {student.submission?.status === 'returned' ? (
                              <span className={cn(badgeBase, statusStyles.returned, "flex items-center gap-1")}>
                                <RotateCcw size={12} />
                                Returned
                              </span>
                            ) : student.status === 'submitted' ? (
                              <span className={cn(badgeBase, statusStyles.submitted, "flex items-center gap-1")}>
                                <CheckCircle2 size={12} />
                                Submitted
                              </span>
                            ) : (
                              <span className={cn(badgeBase, "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700 flex items-center gap-1")}>
                                <XCircle size={12} />
                                Not Submitted
                              </span>
                            )}
                          </div>
                          {student.hasGrade && (
                            <span className={cn(badgeBase, "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700")}>
                              Graded
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* Grading Area */}
          <div className={cn(card, "lg:col-span-2 p-6")}>
            {assignment.isUngraded && (
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-sm">
                <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
                  <XCircle size={20} />
                  <div>
                    <p className="font-medium">This assignment is ungraded</p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-500 mt-1">
                      This assignment is for practice only and cannot be scored or graded.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!selectedStudent ? (
              <div className={cn(emptyState)}>
                <p className="text-gray-500 dark:text-gray-400">Select a student to view their submission</p>
              </div>
            ) : assignmentType === 'subjective' ? (
              <div className="space-y-6">
                {/* Student header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold dark:text-white">{selectedStudent.name}</h2>
                    <p className={bodySecondary}>{selectedStudent.rollNo}</p>
                  </div>
                  {selectedStudent.submission && (
                    <div className="text-right">
                      {selectedStudent.submission.status === 'returned' ? (
                        <span className={cn(badgeBase, statusStyles.returned, "inline-flex items-center gap-1")}>
                          <RotateCcw size={14} />
                          Returned
                        </span>
                      ) : (
                        <span className={cn(badgeBase, statusStyles.submitted, "inline-flex items-center gap-1")}>
                          <CheckCircle2 size={14} />
                          Submitted
                        </span>
                      )}
                      {selectedStudent.submission.submissionDate && (
                        <p className={cn(bodySecondary, "mt-1 flex items-center justify-end gap-1")}>
                          <Clock size={12} />
                          {new Date(selectedStudent.submission.submissionDate).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {!selectedStudent.submission ? (
                  <div className={cn(emptyState)}>
                    <XCircle className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">Student has not submitted the assignment</p>
                  </div>
                ) : selectedStudent.submission.status === 'returned' ? (
                  <div className="text-center py-12 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-sm">
                    <RotateCcw className="mx-auto h-10 w-10 text-orange-400 mb-3" />
                    <p className="font-medium text-orange-800 dark:text-orange-400">Submission Returned to Student</p>
                    {selectedStudent.submission.revertReason && (
                      <div className="mt-3 mx-auto max-w-md text-left p-3 bg-white dark:bg-gray-800 rounded-sm border border-orange-200 dark:border-orange-700">
                        <p className="text-xs font-medium text-orange-700 dark:text-orange-400 mb-1">Reason given:</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedStudent.submission.revertReason}</p>
                      </div>
                    )}
                    {selectedStudent.submission.revertedAt && (
                      <p className="text-xs text-orange-500 dark:text-orange-400 mt-2">
                        Returned on: {new Date(selectedStudent.submission.revertedAt).toLocaleString()}
                      </p>
                    )}
                    <p className={cn(bodySecondary, "mt-3")}>Waiting for student to resubmit.</p>
                  </div>
                ) : (
                  <>
                    {/* Tabs Section - Show if submission file exists */}
                    {selectedStudent.submission.submissionFile && (
                      <div className={cn("border-b mb-4", dividerInner)}>
                        <nav className="flex space-x-1" aria-label="Tabs">
                          <button
                            onClick={() => setActiveTab('answers')}
                            className={cn(
                              "px-4 py-3 text-sm font-medium transition-colors",
                              activeTab === 'answers'
                                ? "border-b-2 border-accent1 text-accent1"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            )}
                          >
                            Answers
                          </button>
                          <button
                            onClick={() => setActiveTab('file')}
                            className={cn(
                              "px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2",
                              activeTab === 'file'
                                ? "border-b-2 border-accent1 text-accent1"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            )}
                          >
                            <Paperclip className="h-4 w-4" />
                            Submission File
                          </button>
                        </nav>
                      </div>
                    )}

                    {/* Submission File Tab */}
                    {activeTab === 'file' && selectedStudent.submission.submissionFile && (
                      <div className={cn(card)} style={{ height: 'calc(100vh - 400px)', minHeight: '500px' }}>
                        <div className="flex flex-col h-full">
                          {/* File Info Header */}
                          <div className={cn(surfaceMuted, "border-b p-4", dividerInner)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="text-accent1" size={20} />
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {selectedStudent.submission.submissionFile.split('/').pop()}
                                  </span>
                                  <p className={cn(bodySecondary, "mt-1")}>
                                    Submitted on: {new Date(selectedStudent.submission.submissionDate).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <a
                                href={selectedStudent.submission.submissionFile}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-accent1 hover:text-accent1/80 px-3 py-2 rounded-sm hover:bg-accent1/10 transition-colors"
                              >
                                <Download size={16} />
                                Download
                              </a>
                            </div>
                          </div>

                          {/* File Viewer */}
                          <div className="flex-1 relative overflow-hidden">
                            {(() => {
                              const fileUrl = selectedStudent.submission.submissionFile;
                              const fileName = fileUrl.split('/').pop();
                              return renderFileViewer(fileUrl, fileName, true);
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Answers Tab */}
                    {activeTab === 'answers' && (
                      <div className="space-y-6">

                        {subjectiveQuestions.map((q, index) => {
                          const answer = selectedStudent.submission.answers?.subjective?.[q._id] || '';
                          return (
                            <div key={q._id} className={cn(card, "p-4")}>
                              <div className="flex items-center gap-2 mb-3">
                                <span className={cn(badgeBase, "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700 font-medium")}>
                                  Q{index + 1}
                                </span>
                                <span className={bodySecondary}>{q.points || 20} points</span>
                              </div>
                              <p className="font-medium text-gray-900 dark:text-white mb-3">{q.question}</p>
                              <div className={cn(surfaceMuted, "rounded-sm p-4 mb-3")}>
                                {answer ? (
                                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{answer}</p>
                                ) : selectedStudent.submission?.submissionFile ? (
                                  <p className="text-accent1 font-medium italic">Please view submitted file</p>
                                ) : (
                                  <p className="text-gray-700 dark:text-gray-300">No answer provided</p>
                                )}
                              </div>
                              {!assignment.isUngraded ? (
                                <div className={cn("flex items-center gap-2", !useAutoCalculate ? 'opacity-50' : '')}>
                                  <input
                                    type="number"
                                    placeholder="Points"
                                    min="0"
                                    max={q.points || 20}
                                    disabled={!useAutoCalculate}
                                    value={questionGrades[getGradeKey(selectedStudent)]?.[q._id] ?? ''}
                                    onChange={(e) => {
                                      const key = getGradeKey(selectedStudent);
                                      const val = e.target.value !== '' ? parseFloat(e.target.value) : null;
                                      setQuestionGrades(prev => ({
                                        ...prev,
                                        [key]: {
                                          ...(prev[key] || {}),
                                          [q._id]: val
                                        }
                                      }));
                                    }}
                                    className={cn(inputClasses, "w-20 disabled:cursor-not-allowed disabled:opacity-60")}
                                  />
                                  <span className={bodySecondary}>/ {q.points || 20}</span>
                                </div>
                              ) : (
                                <p className={cn(bodySecondary, "italic")}>Ungraded assignment - no scoring</p>
                              )}
                            </div>
                          );
                        })}

                        <div className={cn("border-t pt-4", dividerInner)}>
                          {/* Show Objective Score if available */}
                          {objectiveQuestions.length > 0 && selectedStudent.submission && (
                            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-sm">
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                                    Auto-Calculated Objective Score
                                  </label>
                                  <p className="text-xs text-blue-700 dark:text-blue-400">
                                    This score is automatically calculated from MCQ answers
                                  </p>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {calculateObjectiveScore(selectedStudent.submission) || 0}
                                  </div>
                                  <div className="text-sm text-blue-600 dark:text-blue-400">
                                    / {selectedStudent.submission.totalObjectivePoints || getTotalObjectivePoints()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {!assignment.isUngraded ? (
                            <>
                              {/* Grading Mode Toggle */}
                              <div className={cn(surfaceMuted, "mb-4 flex items-center justify-between p-3 rounded-sm border", dividerInner)}>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Grading Mode</span>
                                <div className="flex items-center gap-2">
                                  <span className={cn("text-xs", !useAutoCalculate ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500')}>Manual Total</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (useAutoCalculate && selectedStudent) {
                                        const key = getGradeKey(selectedStudent);
                                        if (manualSubjectiveGrades[key] === undefined) {
                                          const autoTotal = getSubjectiveAutoTotal(key);
                                          if (autoTotal > 0) {
                                            setManualSubjectiveGrades(prev => ({ ...prev, [key]: autoTotal }));
                                          }
                                        }
                                      }
                                      setUseAutoCalculate(prev => !prev);
                                    }}
                                    className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", useAutoCalculate ? 'bg-accent1' : 'bg-gray-300 dark:bg-gray-600')}
                                  >
                                    <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", useAutoCalculate ? 'translate-x-6' : 'translate-x-1')} />
                                  </button>
                                  <span className={cn("text-xs", useAutoCalculate ? 'font-semibold text-accent1' : 'text-gray-400 dark:text-gray-500')}>Auto-calculate</span>
                                </div>
                              </div>

                              {useAutoCalculate ? (
                                <>
                                  {/* Auto-calculated Subjective Total */}
                                  <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-sm">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <label className="block text-sm font-medium text-purple-900 dark:text-purple-300 mb-1">
                                          Subjective Score (Auto-calculated)
                                        </label>
                                        <p className="text-xs text-purple-700 dark:text-purple-400">
                                          Sum of individual question scores above
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                          {getSubjectiveAutoTotal(getGradeKey(selectedStudent))}
                                        </div>
                                        <div className="text-sm text-purple-600 dark:text-purple-400">
                                          / {getTotalSubjectivePoints()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Overall Total (Objective + Subjective) */}
                                  {objectiveQuestions.length > 0 && (
                                    <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-sm">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <label className="block text-sm font-medium text-green-900 dark:text-green-300 mb-1">
                                            Total Grade (Auto-calculated)
                                          </label>
                                          <p className="text-xs text-green-700 dark:text-green-400">
                                            Objective + Subjective scores
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {(calculateObjectiveScore(selectedStudent.submission) || 0) + getSubjectiveAutoTotal(getGradeKey(selectedStudent))}
                                          </div>
                                          <div className="text-sm text-green-600 dark:text-green-400">
                                            / {assignment.totalPoints}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* Manual Subjective Grade Input */}
                                  <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-sm">
                                    <label className="block text-sm font-medium text-purple-900 dark:text-purple-300 mb-2">
                                      Subjective Grade (Manual)
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        placeholder="Enter subjective total"
                                        min="0"
                                        max={getTotalSubjectivePoints()}
                                        value={manualSubjectiveGrades[getGradeKey(selectedStudent)] ?? ''}
                                        onChange={(e) => {
                                          const key = getGradeKey(selectedStudent);
                                          const val = e.target.value !== '' ? parseFloat(e.target.value) : null;
                                          setManualSubjectiveGrades(prev => ({ ...prev, [key]: val }));
                                        }}
                                        className={cn(inputClasses, "flex-1")}
                                      />
                                      <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">/ {getTotalSubjectivePoints()}</span>
                                    </div>
                                  </div>

                                  {/* Overall Total (Objective + Manual Subjective) */}
                                  {objectiveQuestions.length > 0 && (
                                    <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-sm">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <label className="block text-sm font-medium text-green-900 dark:text-green-300 mb-1">
                                            Total Grade
                                          </label>
                                          <p className="text-xs text-green-700 dark:text-green-400">
                                            Objective + Manual subjective
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {(calculateObjectiveScore(selectedStudent.submission) || 0) + (manualSubjectiveGrades[getGradeKey(selectedStudent)] || 0)}
                                          </div>
                                          <div className="text-sm text-green-600 dark:text-green-400">
                                            / {assignment.totalPoints}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              <div className="mb-4">
                                <label className={cn(microLabel, "mb-2 block")}>
                                  Feedback
                                </label>
                                <textarea
                                  value={feedback[getGradeKey(selectedStudent)] || selectedStudent.submission?.feedback || ''}
                                  onChange={(e) => handleFeedbackChange(getGradeKey(selectedStudent), e.target.value)}
                                  className={inputClasses}
                                  rows="4"
                                  placeholder="Add feedback for the student..."
                                />
                              </div>
                              <button
                                onClick={() => handleSaveGrade(selectedStudent)}
                                className={cn(btnPrimary, "w-full py-3")}
                              >
                                Save Grade
                              </button>
                              {selectedStudent.submission && selectedStudent.submission.status !== 'returned' && (
                                <button
                                  onClick={() => {
                                    setRevertReason('');
                                    setShowRevertModal(true);
                                  }}
                                  className="w-full mt-2 py-3 rounded-sm bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors inline-flex items-center justify-center gap-2"
                                >
                                  <RotateCcw size={16} />
                                  Return to Student
                                </button>
                              )}
                            </>
                          ) : (
                            <div className={cn(surfaceMuted, "mb-4 p-4 rounded-sm border", dividerInner)}>
                              <p className={bodySecondary}>
                                This assignment is ungraded. You can still provide feedback to students, but no scores will be recorded.
                              </p>
                              <div className="mt-4">
                                <label className={cn(microLabel, "mb-2 block")}>
                                  Feedback (Optional)
                                </label>
                                <textarea
                                  value={feedback[getGradeKey(selectedStudent)] || selectedStudent.submission?.feedback || ''}
                                  onChange={(e) => handleFeedbackChange(getGradeKey(selectedStudent), e.target.value)}
                                  className={inputClasses}
                                  rows="4"
                                  placeholder="Add feedback for the student..."
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : assignmentType === 'objective' ? (
              <div className="space-y-6">
                {/* Student header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold dark:text-white">{selectedStudent.name}</h2>
                    <p className={bodySecondary}>{selectedStudent.rollNo}</p>
                  </div>
                  {selectedStudent.submission && (
                    <div className="text-right">
                      {selectedStudent.submission.status === 'returned' ? (
                        <span className={cn(badgeBase, statusStyles.returned, "inline-flex items-center gap-1 mb-2")}>
                          <RotateCcw size={14} />
                          Returned
                        </span>
                      ) : (
                        <>
                          <span className={cn(badgeBase, statusStyles.submitted, "inline-flex items-center gap-1 mb-2")}>
                            <CheckCircle2 size={14} />
                            Submitted
                          </span>
                          {selectedStudent.submission.submissionDate && (
                            <p className={cn(bodySecondary, "mb-2 flex items-center justify-end gap-1")}>
                              <Clock size={12} />
                              {new Date(selectedStudent.submission.submissionDate).toLocaleString()}
                            </p>
                          )}
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {calculateObjectiveScore(selectedStudent.submission) || 0}
                          </div>
                          <div className={bodySecondary}>
                            / {selectedStudent.submission.totalObjectivePoints || getTotalObjectivePoints()} points
                          </div>
                          {selectedStudent.submission.objectiveScore !== undefined && (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              (Auto-calculated)
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {!selectedStudent.submission ? (
                  <div className={cn(emptyState)}>
                    <XCircle className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">Student has not submitted the assignment</p>
                  </div>
                ) : selectedStudent.submission.status === 'returned' ? (
                  <div className="text-center py-12 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-sm">
                    <RotateCcw className="mx-auto h-10 w-10 text-orange-400 mb-3" />
                    <p className="font-medium text-orange-800 dark:text-orange-400">Submission Returned to Student</p>
                    {selectedStudent.submission.revertReason && (
                      <div className="mt-3 mx-auto max-w-md text-left p-3 bg-white dark:bg-gray-800 rounded-sm border border-orange-200 dark:border-orange-700">
                        <p className="text-xs font-medium text-orange-700 dark:text-orange-400 mb-1">Reason given:</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedStudent.submission.revertReason}</p>
                      </div>
                    )}
                    {selectedStudent.submission.revertedAt && (
                      <p className="text-xs text-orange-500 dark:text-orange-400 mt-2">
                        Returned on: {new Date(selectedStudent.submission.revertedAt).toLocaleString()}
                      </p>
                    )}
                    <p className={cn(bodySecondary, "mt-3")}>Waiting for student to resubmit.</p>
                  </div>
                ) : (
                  <>
                    {/* Tabs Section - Show if submission file exists */}
                    {selectedStudent.submission.submissionFile && (
                      <div className={cn("border-b mb-4", dividerInner)}>
                        <nav className="flex space-x-1" aria-label="Tabs">
                          <button
                            onClick={() => setActiveTab('answers')}
                            className={cn(
                              "px-4 py-3 text-sm font-medium transition-colors",
                              activeTab === 'answers'
                                ? "border-b-2 border-accent1 text-accent1"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            )}
                          >
                            Answers
                          </button>
                          <button
                            onClick={() => setActiveTab('file')}
                            className={cn(
                              "px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2",
                              activeTab === 'file'
                                ? "border-b-2 border-accent1 text-accent1"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            )}
                          >
                            <Paperclip className="h-4 w-4" />
                            Submission File
                          </button>
                        </nav>
                      </div>
                    )}

                    {/* Submission File Tab */}
                    {activeTab === 'file' && selectedStudent.submission.submissionFile && (
                      <div className={cn(card, "mb-6")} style={{ height: 'calc(100vh - 400px)', minHeight: '500px' }}>
                        <div className="flex flex-col h-full">
                          {/* File Info Header */}
                          <div className={cn(surfaceMuted, "border-b p-4", dividerInner)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="text-accent1" size={20} />
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {selectedStudent.submission.submissionFile.split('/').pop()}
                                  </span>
                                  <p className={cn(bodySecondary, "mt-1")}>
                                    Submitted on: {new Date(selectedStudent.submission.submissionDate).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <a
                                href={selectedStudent.submission.submissionFile}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-accent1 hover:text-accent1/80 px-3 py-2 rounded-sm hover:bg-accent1/10 transition-colors"
                              >
                                <Download size={16} />
                                Download
                              </a>
                            </div>
                          </div>

                          {/* File Viewer */}
                          <div className="flex-1 relative overflow-hidden">
                            {(() => {
                              const fileUrl = selectedStudent.submission.submissionFile;
                              const fileName = fileUrl.split('/').pop();
                              return renderFileViewer(fileUrl, fileName, true);
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Answers Tab */}
                    {activeTab === 'answers' && (
                      <div className="space-y-6">
                        <div className="space-y-4">
                          {objectiveQuestions.map((q, index) => {
                            const qId = q._id || q.id;
                            const studentAnswer = selectedStudent.submission.answers?.objective?.[qId];
                            const isCorrect = studentAnswer === q.correctAnswer;

                            return (
                              <div
                                key={q._id}
                                className={cn(
                                  card,
                                  "p-4",
                                  isCorrect
                                    ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10"
                                    : "border-red-500 bg-red-50/50 dark:bg-red-900/10"
                                )}
                              >
                                <div className="flex items-center gap-2 mb-3">
                                  <span className={cn(badgeBase, "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700 font-medium")}>
                                    Q{index + 1}
                                  </span>
                                  <span className={bodySecondary}>{q.points || 10} points</span>
                                  {isCorrect ? (
                                    <span className={cn(badgeBase, "ml-auto text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700 font-medium")}>
                                      Correct
                                    </span>
                                  ) : (
                                    <span className={cn(badgeBase, "ml-auto text-red-700 bg-red-100 border-red-300 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700 font-medium")}>
                                      Incorrect
                                    </span>
                                  )}
                                </div>
                                <p className="font-medium text-gray-900 dark:text-white mb-3">{q.question}</p>
                                <div className="space-y-2">
                                  {q.options?.map((option, optIndex) => {
                                    const isSelected = studentAnswer === option;
                                    const isCorrectOption = q.correctAnswer === option;

                                    return (
                                      <div
                                        key={optIndex}
                                        className={cn(
                                          "p-3 rounded-sm border",
                                          isCorrectOption
                                            ? "bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700"
                                            : isSelected
                                            ? "bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700"
                                            : "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600"
                                        )}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium dark:text-white">
                                            {String.fromCharCode(65 + optIndex)}.
                                          </span>
                                          <span className="dark:text-gray-200">{option}</span>
                                          {isCorrectOption && (
                                            <CheckCircle2 className="ml-auto text-green-600 dark:text-green-400" size={16} />
                                          )}
                                          {isSelected && !isCorrectOption && (
                                            <XCircle className="ml-auto text-red-600 dark:text-red-400" size={16} />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-3 text-sm">
                                  <span className="text-gray-600 dark:text-gray-400">Student Answer: </span>
                                  <span className={cn("font-medium", isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                                    {studentAnswer || 'Not answered'}
                                  </span>
                                  {!isCorrect && (
                                    <>
                                      <span className="text-gray-600 dark:text-gray-400 ml-3">Correct Answer: </span>
                                      <span className="font-medium text-green-600 dark:text-green-400">{q.correctAnswer}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className={cn("border-t pt-4", dividerInner)}>
                          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-sm">
                            <div className="flex items-center justify-between">
                              <div>
                                <label className="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                                  Auto-Calculated Objective Score
                                </label>
                                <p className="text-xs text-blue-700 dark:text-blue-400">
                                  This score is automatically calculated from MCQ answers
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                  {calculateObjectiveScore(selectedStudent.submission) || 0}
                                </div>
                                <div className="text-sm text-blue-600 dark:text-blue-400">
                                  / {selectedStudent.submission.totalObjectivePoints || getTotalObjectivePoints()}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className={cn(microLabel, "mb-2 block")}>
                              Total Grade
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                placeholder="Total grade"
                                min="0"
                                max={assignment.totalPoints}
                                value={grades[getGradeKey(selectedStudent)] || selectedStudent.submission?.grade || ''}
                                onChange={(e) => handleGradeChange(getGradeKey(selectedStudent), e.target.value)}
                                className={cn(inputClasses, "flex-1")}
                              />
                              <span className={bodySecondary}>
                                / {assignment.totalPoints}
                              </span>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className={cn(microLabel, "mb-2 block")}>
                              Feedback
                            </label>
                            <textarea
                              value={feedback[getGradeKey(selectedStudent)] || selectedStudent.submission?.feedback || ''}
                              onChange={(e) => handleFeedbackChange(getGradeKey(selectedStudent), e.target.value)}
                              className={inputClasses}
                              rows="4"
                              placeholder="Add feedback for the student..."
                            />
                          </div>
                          <button
                            onClick={() => handleSaveGrade(selectedStudent)}
                            className={cn(btnPrimary, "w-full py-3")}
                          >
                            Save Grade
                          </button>
                          {selectedStudent.submission && selectedStudent.submission.status !== 'returned' && (
                            <button
                              onClick={() => {
                                setRevertReason('');
                                setShowRevertModal(true);
                              }}
                              className="w-full mt-2 py-3 rounded-sm bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors inline-flex items-center justify-center gap-2"
                            >
                              <RotateCcw size={16} />
                              Return to Student
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Revert Submission Modal — rendered via portal */}
      {showRevertModal && selectedStudent && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowRevertModal(false)}
          />
          {/* Panel */}
          <motion.div
            {...motionFadeUp}
            className={cn(card, "relative w-full max-w-md shadow-xl mx-4")}
          >
            {/* Header */}
            <div className={cn("flex items-center justify-between p-4 border-b", dividerInner)}>
              <h3 className={cn(cardTitle, "flex items-center gap-2")}>
                <RotateCcw size={18} className="text-orange-500" />
                Return Submission to Student
              </h3>
              <button
                onClick={() => setShowRevertModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
              >
                <X size={20} className="text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4">
              <p className={cn(bodySecondary, "mb-1")}>
                Returning <strong className="text-gray-900 dark:text-white">{selectedStudent.name}'s</strong> submission. This will clear grades and allow the student to resubmit.
              </p>
              <label className={cn(microLabel, "mt-3 mb-2 block")}>
                Reason (required)
              </label>
              <textarea
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                className={cn(inputClasses, "focus:ring-orange-500/50")}
                rows="3"
                placeholder="e.g. Your answer for Q2 seems copied. Please redo in your own words."
              />
            </div>

            {/* Footer */}
            <div className={cn(surfaceMuted, "flex gap-2 p-4 border-t", dividerInner)}>
              <button
                onClick={() => setShowRevertModal(false)}
                className={cn(btnSecondary, "flex-1")}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!revertReason.trim()) {
                    toast.error('Please provide a reason for reverting');
                    return;
                  }
                  try {
                    setIsLoading(true);
                    await revertSubmission(assignmentId, selectedStudent.submission._id, revertReason.trim());
                    setShowRevertModal(false);
                    setRevertReason('');
                    await fetchData();
                    setSelectedStudent(null);
                  } catch (error) {
                    console.error('Error reverting submission:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={!revertReason.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw size={16} />
                Return Submission
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && selectedStudent?.submission?.submissionFile && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-bold">
                {selectedStudent.submission.submissionFile.split('/').pop()}
              </h3>
              <span className="text-sm text-gray-300">
                {selectedStudent.name} - {selectedStudent.rollNo}
              </span>
            </div>
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-2 hover:bg-gray-700 rounded-sm transition-colors"
              title="Exit Fullscreen (Esc)"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Fullscreen Viewer */}
          <div className="flex-1 overflow-auto">
            {renderFileViewer(
              selectedStudent.submission.submissionFile,
              selectedStudent.submission.submissionFile.split('/').pop(),
              false
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAssignmentGrading;
